import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PrimaryCard from "../common/cards/PrimaryCard";
import { useAlert, useLocation, useWeather } from "../../hooks";
// eslint-disable-next-line import/no-webpack-loader-syntax
import maplibregl from "!maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  Box,
  Grid,
  GridItem,
  Icon,
  TagLabel,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { PrimaryTag } from "../common/tags";
import { DestinationIcon, RoadIcon } from "../common/icons";
import PositionIcon from "../common/icons/PositionIcon";
import { Trans, useTranslation } from "react-i18next";
import CopyIcon from "../common/icons/CopyIcon";
import { withIdentityPoolId } from "@aws/amazon-location-utilities-auth-helper";
import { useAuth } from "../../../hooks"

const DEFAULT_INITIAL_ZOOM = 16;
const DEFAULT_WHEEL_ZOOM_RATE = 1 / 450;
const DEFAULT_ZOOM_RATE = 1 / 100;

/**
 * Renders the location card component
 * 
 * @returns the location card component
 */
const LocationCard = () => {
  const { t } = useTranslation();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const vehicleMarkerRef = useRef(null);
  const vehicleHoverMarkerRef = useRef(null);
  const {
    currentAddress,
    destinationAddress,
    currentLatitude,
    currentLongitude,
    destinationLatitude,
    destinationLongitude,
    route,
  } = useLocation();
  const { icon, temperature } = useWeather();
  const { alert } = useAlert();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const { fetchBearer } = useAuth();

  /**
   * Handles the mouse enter event
   */
  const onMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, []);

  /**
   * Handles the mouse leave event
   */
  const onMouseLeave = useCallback(() => {
    setIsHovering(false);
  }, []);

  const degreeTemperature = useMemo(
    () => (temperature - 273.15).toFixed(),
    [temperature],
  );

  useEffect(() => {
    if (vehicleMarkerRef.current != null && vehicleHoverMarkerRef.current != null) {
      if (isHovering) {
        vehicleMarkerRef.current.style.opacity = 0;
        vehicleHoverMarkerRef.current.style.opacity = 1;
      } else {
        vehicleMarkerRef.current.style.opacity = 1;
        vehicleHoverMarkerRef.current.style.opacity = 0;
      }
    }
  }, [isHovering])

  useEffect(() => {
    const initMap = async () => {
      const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
      const identityPoolId = process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID;
      const bearer = await fetchBearer()
      const authHelper = await withIdentityPoolId(identityPoolId, {
        logins: {
          [`cognito-idp.${process.env.REACT_APP_COGNITO_REGION}.amazonaws.com/${userPoolId}`]: bearer
        }
      });

      if (mapContainerRef.current) {
        mapRef.current = new maplibregl.Map({
          attributionControl: false,
          container: mapContainerRef.current,
          center: [0, 0],
          pitch: 60,
          preserveDrawingBuffer: true,
          zoom: DEFAULT_INITIAL_ZOOM,
          style: `https://maps.geo.${process.env.REACT_APP_LOCATION_MAP_REGION}.amazonaws.com/maps/v0/maps/${process.env.REACT_APP_LOCATION_MAP_NAME}/style-descriptor`, // Defines the appearance of the map and authenticates using an API key

          // style: `https://maps.geo.${process.env.REACT_APP_LOCATION_MAP_REGION}.amazonaws.com/v2/styles/Standard/descriptor?color-scheme=Dark`,
          ...authHelper.getMapAuthenticationOptions(),
        });
        mapRef.current.scrollZoom.setWheelZoomRate(DEFAULT_WHEEL_ZOOM_RATE * 15);
        mapRef.current.scrollZoom.setZoomRate(DEFAULT_ZOOM_RATE * 15);
        setIsInitialized(true);
      }
    }

    initMap();

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, [fetchBearer]);

  useEffect(() => {
    if (isInitialized) {
      mapRef.current.on("load", () => {
        setHasLoaded(true);
        try {
          mapRef.current.removeLayer("route");
          mapRef.current.removeSource("route");
        } catch (error) {
          console.error(error);
        }
        mapRef.current.addSource("route", {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: route,
            },
          },
        });
        mapRef.current.addLayer({
          id: "route",
          type: "line",
          source: "route",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#1a73e8",
            "line-width": 2,
          }
        });
      });
    }
  }, [route, destinationLatitude, destinationLongitude, isInitialized])

  useEffect(() => {
    if (isInitialized) {
      vehicleMarkerRef.current = document.createElement("div")
      vehicleMarkerRef.current.className = "marker";
      vehicleMarkerRef.current.style.backgroundImage = "url(/marker.png)";
      vehicleMarkerRef.current.style.width = "33px";
      vehicleMarkerRef.current.style.height = "41px";
      vehicleMarkerRef.current.style.opacity = 1;
      vehicleHoverMarkerRef.current = document.createElement("div")
      vehicleHoverMarkerRef.current.className = "marker";
      vehicleHoverMarkerRef.current.style.backgroundImage = "url(/red_marker.png)";
      vehicleHoverMarkerRef.current.style.width = "33px";
      vehicleHoverMarkerRef.current.style.height = "41px";
      vehicleHoverMarkerRef.current.style.opacity = 0;

      new maplibregl.Marker({ element: vehicleMarkerRef.current })
        .setLngLat([currentLongitude || 0, currentLatitude || 0])
        .addTo(mapRef.current);
      new maplibregl.Marker({ element: vehicleHoverMarkerRef.current })
        .setLngLat([currentLongitude || 0, currentLatitude || 0])
        .addTo(mapRef.current);
      mapRef.current.setCenter([currentLongitude || 0, currentLatitude || 0])
      if (hasLoaded) {
        mapRef.current.removeLayer("route");
        mapRef.current.removeSource("route");
        mapRef.current.addSource("route", {
          "type": "geojson",
          "data": {
            "type": "Feature",
            "properties": {},
            "geometry": {
              "type": "LineString",
              "coordinates": route,
            }
          }
        });
        mapRef.current.addLayer({
          "id": "route",
          "type": "line",
          "source": "route",
          "layout": {
            "line-join": "round",
            "line-cap": "round",
          },
          "paint": {
            "line-color": "#1a73e8",
            "line-width": 2,
          }
        });
      }
    }
    return () => {
      if (vehicleMarkerRef.current) {
        vehicleMarkerRef.current.remove();
      }
    };
  }, [currentLongitude, currentLatitude, hasLoaded, route, isInitialized]);

  const typeOfRoad = useMemo(() => {
    if (currentAddress?.Street === "Convention Center Dr" || currentAddress?.Street === "Rue Daniel Constantini") {
      return "parking";
    }
    return currentAddress?.Street?.split(" ")[0];
  }, [currentAddress]);

  const isHighway = useMemo(() => {
    const autorouteRegex = /^[A]\d+$/;

    return autorouteRegex.test(typeOfRoad) || typeOfRoad?.includes("Autoroute");
  }, [typeOfRoad]);

  /**
   * Copies the content to the clipboard
   * 
   * @param {Object} event - The click event
   * @param {string} content - The content to copy
   */
  const copyToClipboard = useCallback(
    (event, content) => {
      navigator.clipboard.writeText(content);
      alert({
        id: "copied",
        title: t("plugin.location.copied"),
        duration: 1000,
      });
    },
    [alert, t],
  );

  return (
    <PrimaryCard title={t("plugin.location.title")}>
      <Box
        borderRadius={"7px"}
        ref={mapContainerRef}
        width={"full"}
        height={"full"}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div ref={mapRef} />
        <Grid
          position={"absolute"}
          width={"full"}
          height={"full"}
          templateColumns={"repeat(12, 1fr)"}
          templateRows={"repeat(11, 1fr)"}
          gap={1}
          padding={2}
        >
          <Tooltip
            label={`${currentLatitude}, ${currentLongitude}`}
            placement={"bottom"}
            minWidth={"150px"}
          >
            <GridItem
              colStart={1}
              colSpan={12}
              rowStart={1}
              rowSpan={1}
              zIndex={100}
            >
              <PrimaryTag icon={PositionIcon} cursor={"pointer"} onClick={() => {
                mapRef.current?.easeTo({
                  center: [currentLongitude || 0, currentLatitude || 0],
                  zoom: DEFAULT_INITIAL_ZOOM,
                  pitch: 60,
                  bearing: 0,
                  duration: 1000,
                });
              }}>
                <TagLabel fontSize={"12px"} fontWeight={300} textAlign={"start"}>
                  {currentAddress?.Label}
                </TagLabel>
                <Text
                  cursor={"pointer"}
                  variant={"ghost"}
                  onClick={(event) =>
                    copyToClipboard(event, `${currentLatitude}, ${currentLongitude}`)
                  }
                  size={"sm"}
                >
                  <Icon as={CopyIcon} color={"white"} boxSize={"15px"} />
                </Text>
              </PrimaryTag>
            </GridItem>
          </Tooltip>
          <Tooltip label={t("plugin.tooltips.weather")} placement={"left"}>
            <GridItem
              colSpan={4}
              colStart={9}
              rowSpan={1}
              rowStart={10}
              zIndex={100}
            >
              <PrimaryTag icon={icon}>
                <TagLabel fontSize={"12px"} fontWeight={300} textAlign={"start"}>
                  {isNaN(degreeTemperature) ? "" : degreeTemperature} °C
                </TagLabel>
              </PrimaryTag>
            </GridItem>
          </Tooltip>
          <Tooltip
            label={t("plugin.tooltips.road", {
              state: isHighway
                ? t("plugin.tooltips.state.is")
                : t("plugin.tooltips.state.isNot"),
            })}
            placement={"right"}
          >
            <GridItem
              colStart={1}
              colSpan={5}
              rowStart={10}
              rowSpan={1}
              zIndex={100}
            >
              <PrimaryTag
                backgroundColor={isHighway ? "#FF000060" : "#5B5F6188"}
                icon={RoadIcon}
              >
                <TagLabel fontSize={"12px"} fontWeight={300} textAlign={"start"}>
                  {isHighway ? (
                    <Trans>plugin.location.typeOfRoad.highway</Trans>
                  ) : typeOfRoad === "parking" ? (
                    <Trans>plugin.location.typeOfRoad.parking</Trans>
                  ) : (
                    <Trans>plugin.location.typeOfRoad.road</Trans>
                  )}
                </TagLabel>
              </PrimaryTag>
            </GridItem>
          </Tooltip>
          <Tooltip
            label={`${destinationLatitude}, ${destinationLongitude}`}
            placement={"bottom"}
            minWidth={"150px"}
          >
            <GridItem
              colStart={1}
              colSpan={12}
              rowStart={11}
              rowSpan={1}
              zIndex={100}
            >
              <PrimaryTag icon={DestinationIcon}>
                <TagLabel fontSize={"12px"} fontWeight={300} textAlign={"start"}>
                  {destinationAddress?.Label}
                </TagLabel>
                <Text
                  cursor={"pointer"}
                  variant={"ghost"}
                  onClick={(event) =>
                    copyToClipboard(event, `${destinationLatitude}, ${destinationLongitude}`)
                  }
                  size={"sm"}
                >
                  <Icon as={CopyIcon} color={"white"} boxSize={"15px"} />
                </Text>
              </PrimaryTag>
            </GridItem>
          </Tooltip>
        </Grid>
      </Box>
    </PrimaryCard>
  );
};

export default React.memo(LocationCard);
